
import AppInput from "@/components/Form/AppInput.vue";
import {
  defineComponent,
  PropType,
  reactive,
  Ref,
  ref,
  toRefs,
  watch,
  computed
} from "vue";

import {
  ILocationPackageListItemEditDto,
  IOptionsPlan
} from "@/hooks/esim/location-packages/types/useLocationPackageListType";
import AppInputsValidationProvider from "@/components/Form/AppInputsValidationProvider.vue";
import {
  IDataType,
  IPlanOperator,
  IPurchaseType,
  IValidity
} from "@/api/services/packages/esim/location-packages/types";
import SelectDataTypes from "@/components/Packages/eSIM/Directory/Selects/SelectDataTypes.vue";
import SelectInput from "@/components/Form/SelectInput.vue";
import SelectBundle from "@/components/Packages/eSIM/Directory/Selects/SelectBundle.vue";
import SelectPurchaseType from "@/components/Packages/eSIM/Directory/Selects/SelectPurchaseType.vue";
import SelectValidityType from "@/components/Packages/eSIM/Directory/Selects/SelectValidityType.vue";
import AppButton from "@/components/ui/Buttons/AppButton.vue";
import Partner from "@/components/ui/Partner.vue";
import { networkGenerationsToString } from "@/store/modules/carriers/utills/networkGenerationsToString";
import { useCollapseAnimation } from "@/hooks/animation/useCollapseAnimation";
import { ITab } from "@/store/interfaces/common";
import TabsDialog from "@/components/ui/Modal/Dialog/TabsDialog.vue";
import { IEsimDirectoryDefault } from "@/api/interfaces/esim-directory/common";
import { IRate } from "@/api/interfaces/rates/common";
import { ILocationRates } from "@/store/locationPackages";
import { useStore } from "vuex";
import { initialLocationData } from "@/hooks/locations/modules/updateLocation/initialLocationData";
import { initialEditData } from "@/views/packages/eSIM/LocationPackages.vue";

const tabs = ["Details", "Carriers"];

export default defineComponent({
  name: "EditLocationPackageDialog",
  components: {
    TabsDialog,
    Partner,
    AppButton,
    SelectValidityType,
    SelectPurchaseType,
    SelectBundle,
    SelectInput,
    SelectDataTypes,
    AppInputsValidationProvider,
    AppInput
  },
  props: {
    opened: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    operators: { type: Array as PropType<IPlanOperator[]>, default: () => [] },
    selectedLocationName: { type: String, default: "" },
    selectedLocationType: { type: String, default: "" },
    locationRates: {
      type: Array as PropType<ILocationRates[]>,
      default: () => []
    },
    rate: {
      type: Object as PropType<IRate>,
      required: true
    },
    getPrice: {
      type: Function,
      required: true
    },
    editData: {
      type: Object as PropType<ILocationPackageListItemEditDto>,
      required: true
    },
    trafficList: {
      type: Array as PropType<IEsimDirectoryDefault[]>,
      required: true
    }
  },
  emits: { hide: null, submit: null },

  setup(props) {
    const store = useStore();
    const { editData, getPrice, rate, trafficList } = toRefs(props);
    const packageModel: ILocationPackageListItemEditDto = reactive({
      ...editData.value,
      selectedLocationType: props.selectedLocationType,
      locationRates: []
    });

    watch(
      () => editData,
      data => {
        Object.entries(data.value).forEach(([key, value]) => {
          packageModel[key] = value;
          if (key === "traffic") {
            if (!value?.id) {
              packageModel[key] = trafficList.value[0];
            }
          }
        });
      },
      { deep: true, immediate: true }
    );

    type TDirectoryData = IDataType | IValidity | IPurchaseType | number;

    function setDirectoryData(
      key: keyof ILocationPackageListItemEditDto,
      value: TDirectoryData
    ) {
      packageModel[key] = value;
    }

    const localDefaultPrice = ref("");

    watch(
      () => packageModel.defaultPrice,
      (price: number) => {        
        if (price.toString() !== localDefaultPrice.value) {
          if (!props.isEdit) {
            localDefaultPrice.value = "";

            return;
          }

          localDefaultPrice.value = price.toString();
        }
      },
      { immediate: true }
    );

    watch(
      () => props.isEdit,
      () => {
        if (!props.isEdit) {
          localDefaultPrice.value = "";
        }
      },
      { immediate: true }
    );

    watch(
      () => props.selectedLocationType,
      () => {
        packageModel.selectedLocationType = props.selectedLocationType;
      }
    );

    watch(
      () => props.locationRates,
      () => {
        packageModel.locationRates = props.locationRates;
      },
      { deep: true }
    );

    watch(localDefaultPrice, (price: string) => {
      if (packageModel.defaultPrice.toString() !== price) {
        packageModel.defaultPrice = Number(price) || 0;
      }
    });

    // Discount types

    const discountValue = ref(
      packageModel.discountAmount ?? packageModel.discountPercent ?? 0
    );

    const discountAvailable: Ref<boolean> = ref(packageModel.discount);
    const packageCommission: Ref<number> = ref(0);

    watch(
      () => editData.value.packageCommission,
      (price: number | null) => {
        packageCommission.value = price || 0;
      }
    );
    watch(
      () => packageCommission.value,
      price => {
        if (
          packageModel.packageCommission !== Number(price) &&
          Number(price) < 101
        ) {
          packageModel.packageCommission = Number(price) || 0;
          packageCommission.value = Number(price) || 0;
        } else if (Number(price) > 100) {
          packageModel.packageCommission = 100;
          packageCommission.value = 100;
        }
      }
    );
    const mappedTraffic = computed(() => {
      return props.trafficList.map((traffic, idx) => ({
        id: traffic.id,
        title: traffic.title,
        amount: traffic.amount,
        selected: !!packageModel.traffic?.id
          ? packageModel.traffic.id === traffic.id
          : idx === 0
      }));
    });

    watch(
      packageModel,
      () => {
        if (packageModel.selectedLocationType === "countries") {
          let monthPrice = 0;
          if (packageModel.validity?.period === 365) {
            const { selectedPlan, traffic, locationId } = packageModel;
            monthPrice = store.getters.getMountPackage(
              selectedPlan,
              traffic.amount,
              locationId,
              props.selectedLocationType
            );
            if (!monthPrice) {
              localDefaultPrice.value = "Error";
              return;
            }
          }
          const { price, priceWithDiscount } = getPrice.value(
            packageModel,
            rate.value,
            monthPrice
          );
          
          if (!isNaN(priceWithDiscount)) {
            localDefaultPrice.value = priceWithDiscount.toString();
            packageModel.defaultPrice = price;
            packageModel.priceWithDiscount = priceWithDiscount;
          } else {
            localDefaultPrice.value = "";
          }
        } else {
          packageModel.defaultPrice = Number(localDefaultPrice.value);
          if (packageModel.discount) {
            packageModel.priceWithDiscount =
              Number(localDefaultPrice.value) -
              (Number(localDefaultPrice.value) / 100) *
                packageModel.discountAmount;
          }
        }
      },
      { deep: true }
    );
    return {
      packageModel,
      discountAvailable,
      discountValue,
      packageCommission,
      localDefaultPrice,
      mappedTraffic,
      selectedBundleId: computed(() => packageModel.bundleId || -1),
      selectedDataTypeId: computed(() => packageModel.dataType?.id || -1),
      selectedValidityTypeId: computed(() => packageModel.validity?.id || -1),
      selectedPurchaseTypeId: computed(
        () => packageModel.purchaseType?.id || -1
      ),
      currentZoneName: computed(() => packageModel.currentZoneName || ""),

      selectDataType(dataType: IDataType) {
        setDirectoryData("dataType", dataType);
      },

      selectPurchaseType(purchaseType: IPurchaseType) {
        setDirectoryData("purchaseType", purchaseType);
      },

      selectValidityType(validityType: IValidity) {
        setDirectoryData("validity", validityType);
      },

      selectBundle(id: number) {
        setDirectoryData("bundleId", id);
        setDirectoryData("zone_i_product", id);
      },

      selectTraffic(id: number) {
        packageModel.traffic =
          trafficList.value.find(el => el.id === id) ?? props.trafficList[0];
      },

      changeDiscountAvailable({ target }: Event) {
        discountAvailable.value = (target as HTMLInputElement).checked || false;
      },

      selectPlan(id: number): void {
        packageModel.currentPlans.forEach((plan: IOptionsPlan) => {
          plan.selected = plan.id === id;
        });
      }
    };
  },

  data(): {
    tabs: string[];
    selectedTab: number;
    validationError: boolean;
    modalLoading: boolean;
    beforeEnter: Function;
    parentTouched: boolean;
    enter: Function;
    leave: Function;
    tabsList: ITab[];
  } {
    return {
      ...useCollapseAnimation,
      validationError: false,
      selectedTab: 0,
      modalLoading: false,
      parentTouched: false,
      tabs,
      tabsList: tabs.map((title, idx) => {
        return { title, id: idx };
      })
    };
  },

  computed: {
    title() {
      return this.isEdit ? "Edit package" : "Add package";
    },

    submitError() {
      if (this.localDefaultPrice === "Error") {
        return true;
      }
      return this.validationError;
    },

    updatedOperators() {
      return this.operators.map((operator: IPlanOperator) => {
        return {
          ...operator,
          networkGenerationString: networkGenerationsToString(
            operator.operator.netGens
          )
        };
      });
    },

    getLabelForInput() {
      return this.packageModel.selectedPlan.discount
        ? "Price with discount in $"
        : "Default price in $";
    }
  },

  watch: {
    opened: {
      handler(opened: boolean) {
        if (!opened) {
          this.selectedTab = 0;
        }
      }
    }
  },

  methods: {
    hide() {
      this.$emit("hide");
    },

    onChangeErrorState(error: boolean) {
      this.validationError = error;
    },

    submitHandler() {
      if (!this.validationError) {
        this.$emit("submit", this.packageModel);
      }
    }
  }
});
